/**
 * src/components/public/layout/FloatingActions.tsx
 *
 * Sağ-alt sabit FAB (floating action button) cluster — Bize Ulaşın + WhatsApp +
 * Telefon + Yukarı-çık. wproit tasarımının Next.js portu.
 *
 * Tasarım:
 *  - En üstte "Bize Ulaşın" birincil CTA: diğerlerinden farklı olarak HER ZAMAN
 *    açık (etiket görünür), marka renginde. Hedef URL next-intl `Link` ile
 *    üretilir — `/iletisim` href'i aktif dile göre otomatik çevrilir
 *    (TR: /tr/iletisim, EN: /en/contact). Segment haritası
 *    siteSettings.routeSegments'ten geldiği için yeni dil eklenince burada
 *    kod değişmez.
 *  - Diğer butonlar 48×48 daire (h-12 w-12); desktop'ta hover/focus'ta sağa doğru
 *    hap (pill) olarak açılır → ikon + etiket görünür. Pill expand SAF CSS
 *    (group-hover/focus + label max-w-0→max-w-xs + opacity). Mobilde hep kapalı
 *    (48×48 dokunma hedefi, WCAG 2.1 SC 2.5.5).
 *  - WhatsApp: marka yeşili (#25D366), wa.me linki. Telefon: nötr, tel: linki.
 *  - Yukarı-çık: scroll > 400px'de yumuşak fade-in + 16px slide-up; gizliyken
 *    pointer-events-none (yer kaplamaz). Tıkla → smooth scroll top.
 *
 * Client Component — back-to-top scroll state + smooth scroll gerekir. WhatsApp
 * ve Telefon koşullu render (URL/numara verilmişse). Etiketler next-intl 'fab'
 * namespace'inden.
 */
'use client'

import { ArrowUp, Mail, Phone } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useEffect, useState } from 'react'

import { trackEvent } from '@/lib/analytics/gtag'
import { Link } from '@/lib/i18n/navigation'

interface FloatingActionsProps {
  /** wa.me linki (mesaj query'si dahil) — yoksa WhatsApp butonu gizlenir. */
  whatsappUrl?: string | null
  /** Görüntülenen telefon ("+90 (312) ...") — yoksa Telefon butonu gizlenir. */
  phone?: string | null
  /** tel: linki ("tel:+90...") — phone ile birlikte gerekir. */
  phoneHref?: string | null
}

/** Yukarı-çık butonunun görünür olacağı scroll eşiği (px). */
const BACK_TO_TOP_THRESHOLD = 400

/** Daire→hap ortak class'ları (hover/focus expand). */
const PILL =
  'group flex items-center justify-end overflow-hidden rounded-full shadow-lg hover:shadow-xl ' +
  'h-12 w-12 sm:hover:w-auto sm:focus:w-auto sm:hover:px-5 sm:focus:px-5'

/** Etiket — kapalıyken max-w-0 + opacity-0, hover/focus'ta açılır. */
const LABEL =
  'hidden sm:inline-block whitespace-nowrap text-sm font-medium pr-3 max-w-0 opacity-0 ' +
  'transition-all duration-200 sm:group-hover:max-w-xs sm:group-focus:max-w-xs ' +
  'sm:group-hover:opacity-100 sm:group-focus:opacity-100'

/** İkon kutusu — sabit 48×48 (daire merkezinde ikon). */
const ICON_WRAP = 'flex-shrink-0 w-12 h-12 flex items-center justify-center'

/**
 * Birincil CTA stili (Bize Ulaşın) — HER ZAMAN açık hap.
 *
 * Diğer FAB'lar hover'da açılırken bu buton kapanmaz: cluster'daki tek
 * dönüşüm hedefi olduğu için kullanıcı hover etmeden ne olduğunu görmeli.
 * Yükseklik h-12 — diğerleriyle aynı hizada durur ve mobilde 48px dokunma
 * hedefini korur (WCAG 2.1 SC 2.5.5).
 */
const CTA_PILL =
  'flex h-12 items-center gap-2 whitespace-nowrap rounded-full bg-brand-600 pl-5 pr-4 ' +
  'text-sm font-semibold text-white shadow-lg transition-colors duration-200 ' +
  'hover:bg-brand-700 hover:shadow-xl'

/** Nötr buton stili (Telefon + Yukarı-çık) — beyaz/slate, hover marka. */
const NEUTRAL =
  'bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 ' +
  'text-slate-600 dark:text-slate-300 hover:text-brand-700 dark:hover:text-brand-300 ' +
  'hover:border-brand-300 dark:hover:border-brand-700'

/**
 * FloatingActions — sağ-alt FAB cluster.
 *
 * @param whatsappUrl wa.me linki (opsiyonel)
 * @param phone görüntülenen telefon (opsiyonel)
 * @param phoneHref tel: linki (opsiyonel)
 */
export function FloatingActions({ whatsappUrl, phone, phoneHref }: FloatingActionsProps) {
  const t = useTranslations('fab')
  const [showTop, setShowTop] = useState(false)

  useEffect(() => {
    // rAF throttle ile 60fps scroll — back-to-top görünürlük eşiği.
    let ticking = false
    const onScroll = () => {
      if (ticking) return
      ticking = true
      requestAnimationFrame(() => {
        setShowTop(window.scrollY > BACK_TO_TOP_THRESHOLD)
        ticking = false
      })
    }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <div
      className="fixed bottom-6 right-4 sm:right-6 z-40 flex flex-col items-end gap-2.5 print:hidden"
      aria-label={t('aria_cluster')}
    >
      {/* Bize Ulaşın — cluster'ın en üstünde, koşulsuz render. WhatsApp/telefon
          site ayarlarında boş olabilir ama iletişim sayfası her zaman var, bu
          yüzden guard yok. href sabit '/iletisim' verilir; next-intl Link'i
          routeSegments üzerinden aktif dilin segmentine çevirir. */}
      <Link
        href="/iletisim"
        aria-label={t('aria_contact')}
        onClick={() => trackEvent('contact_click', { location: 'fab' })}
        className={CTA_PILL}
      >
        <span>{t('contact')}</span>
        <Mail className="h-5 w-5 flex-shrink-0" aria-hidden />
      </Link>

      {whatsappUrl && (
        <a
          href={whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={t('aria_whatsapp')}
          onClick={() => trackEvent('whatsapp_click', { location: 'fab' })}
          className={`${PILL} bg-[#25D366] text-white transition-all duration-200`}
        >
          <span className={LABEL}>{t('whatsapp')}</span>
          <span className={ICON_WRAP}>
            {/* WhatsApp resmi glyph — lucide'de yok, inline SVG. */}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              viewBox="0 0 24 24"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
            </svg>
          </span>
        </a>
      )}

      {phone && phoneHref && (
        <a
          href={phoneHref}
          aria-label={t('aria_phone')}
          onClick={() => trackEvent('phone_click', { location: 'fab' })}
          className={`${PILL} ${NEUTRAL} transition-all duration-200`}
        >
          <span className={LABEL}>{phone}</span>
          <span className={ICON_WRAP}>
            <Phone className="h-5 w-5" aria-hidden />
          </span>
        </a>
      )}

      {/* Yukarı-çık — scroll>400px'de görünür; gizliyken pointer-events-none.
          Gizliyken tabIndex=-1 + aria-hidden ŞART: sadece opacity-0 verilince
          buton DOM'da kalıp Tab sırasında hedef oluyordu — klavye kullanıcısı
          hiçbir görsel fokus göstergesi olmayan bir düğmeye takılıyordu
          (WCAG 2.4.7 Focus Visible). */}
      <button
        type="button"
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        aria-label={t('aria_back_to_top')}
        tabIndex={showTop ? 0 : -1}
        aria-hidden={!showTop}
        className={`${PILL} ${NEUTRAL} ease-out transition-[opacity,transform,width,padding,box-shadow,border-color,color] duration-300 ${
          showTop
            ? 'translate-y-0 opacity-100 pointer-events-auto'
            : 'translate-y-4 opacity-0 pointer-events-none'
        }`}
      >
        <span className={LABEL}>{t('back_to_top')}</span>
        <span className={ICON_WRAP}>
          <ArrowUp className="h-5 w-5" aria-hidden />
        </span>
      </button>
    </div>
  )
}
